import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { skipToken } from '@reduxjs/toolkit/query';

import type {
  ApiListingRequest,
  TablePageSelectorProps,
  TableWrapperRef,
} from '@bitstopco/bitstop-theme';
import { TablePageSelector, TableWrapper, useModal } from '@bitstopco/bitstop-theme';
import omit from 'lodash/omit';
import { useParams } from 'react-router-dom';

import SalesStatsCards from '@/components/ui/SalesStatsCards';
import CardTable from '@/components/ui/card/CardTable';
import {
  useGetTransactionsExportMutation,
  useGetTransactionsQuery,
  useGetTransactionsStatsQuery,
} from '@/store/api/services/transactions/transactions';

import { DEFAULT_PAGINATION } from '@/constants/api';
import { MACHINE_SALES_STATS } from '@/constants/app/machine';
import { getFilteredMachineSalesConfig } from '@/helpers/roleBasedFiltering';

import type { Tag, TransactionsSale, WithTags } from '@/types';

import { appendToFilters } from '@/helpers/api';
import { useAppSelector } from '@/store';
import { authReducer } from '@bitstopco/bitstop-theme';

/**
 * Machines Sales Table
 * * For tables we have the TableWrapper component from the Bitstop Theme, for more information please visit the Bitstop Theme documentation
 * @return {*}
 */
const MachineSales = () => {
  const { id = '0', saleId } = useParams();
  const { show } = useModal();

  const [exportMutation] = useGetTransactionsExportMutation();

  const [payload, setPayload] = useState<ApiListingRequest>();

  const tableRef = useRef<TableWrapperRef>(null);

  // Get user role for column filtering
  const {
    selectors: { userInfo },
  } = authReducer;
  const userData = useAppSelector(userInfo);
  const userRoleName = userData?.role?.name;

  // Get filtered columns and filters based on user role
  const { columns: filteredColumns, filters: filteredFilters } = useMemo(() => {
    return getFilteredMachineSalesConfig(userRoleName);
  }, [userRoleName]);

  const computedPayload = useMemo(() => {
    if (!payload) {
      return skipToken;
    }
    return appendToFilters(payload, [
      {
        operator: 'equal',
        property: 'atm_id',
        value: id,
      },
    ]);
  }, [payload, id]);

  const statsPayload = useMemo(() => {
    if (typeof computedPayload === 'symbol') {
      return computedPayload;
    }

    return omit(computedPayload, ['pagination', 'page', 'items_per_page']);
  }, [computedPayload]);

  /**
   * Get the Machine transactions filtered by the desired payload
   */
  const {
    data: {
      data: apiData = [],
      included,
      meta: { pagination: metaPagination = DEFAULT_PAGINATION } = {},
    } = {},
    isFetching,
  } = useGetTransactionsQuery(computedPayload);

  const {
    data: { data: stats } = {},
  } = useGetTransactionsStatsQuery(statsPayload);

  const data: WithTags<TransactionsSale>[] = useMemo(() => {
    const allTags = (included?.tags ?? []) as Tag[];
    return apiData.map((customer) => ({
      ...customer,
      tags: allTags.filter(({ id }) => customer.tag_ids.includes(id)),
    }));
  }, [apiData]);

  useEffect(() => {
    /**
     * If a saleId is preselected, we want to show the machine transaction details modal
     */
    if (saleId) {
      handleRow({ id: saleId } as TransactionsSale);
    }
  }, []);

  const handleRow = ({ id }: TransactionsSale) => {
    /**
     * Show the machine transaction details modal
     */
    show('transaction-details', {
      data: {
        id,
        hideAtmValue: true,
      },
    });
  };

  // Custom Page Selector, this is necessary since we are using a custom SalesStatsCards
  const renderPageSelector = useCallback(
    (props: TablePageSelectorProps) => (
      <>
        <SalesStatsCards stats={stats} mapData={MACHINE_SALES_STATS} sx={{ mx: 3, mb: 2 }} />
        <TablePageSelector {...props} />
      </>
    ),
    [stats],
  );

  const handleExport = (type: string) => {
    // Use the getExportFile helper to export the Table data using the current payload
    tableRef.current?.getExportFile({
      type,
      mutation: exportMutation,
      overridePayload: computedPayload as ApiListingRequest,
      fileName: 'Machine-Sales-Report',
    });
  };

  return (
    <CardTable sx={{ mt: 2 }}>
      <TableWrapper<WithTags<TransactionsSale>>
        data={data}
        ref={tableRef}
        dateRangeFilterKey="created_at"
        sortByDefaultValue="created_at"
        isLoading={isFetching || !payload}
        columns={filteredColumns}
        filterConfigs={filteredFilters}
        tableToolbarProps={{
          searchPlaceholder: 'Search by Sale ID, TX Hash, or Wallet Address',
        }}
        renderPageSelector={renderPageSelector}
        totalPages={metaPagination?.total_pages}
        totalItems={metaPagination?.total_items}
        onActionMenuItemClick={handleExport}
        onChange={setPayload}
        tableProps={{
          placeholder: {
            empty: {
              title: 'No sales found',
              content:
                "We can't find any sale that matches your search, please try a different request",
            },
          },
        }}
        // The URL rewrite is left to the /machine/:id/sales/:saleId deep link above.
        onRowClick={({ id: rowSaleId }: TransactionsSale) =>
          show('transaction-details', {
            hideUrlParams: true,
            data: { id: rowSaleId, hideAtmValue: true },
          })
        }
      />
    </CardTable>
  );
};

export default MachineSales;
